// src/components/AnnouncementBar.tsx
import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

interface AnnouncementBarProps {
  startDate: string;
  endDate: string;
  messages: React.ReactNode[];
  isVisibleAtTop: boolean;
}

const AnnouncementBar: React.FC<AnnouncementBarProps> = ({
  startDate,
  endDate,
  messages,
  isVisibleAtTop,
}) => {
  const [show, setShow] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    setShow(now >= start && now <= end);
  }, [startDate, endDate]);

  useEffect(() => {
    if (!show || messages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % messages.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [show, messages]);

  if (!show || !isVisibleAtTop) return null; 

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      className="relative w-full z-50 overflow-hidden"
    >
      <div
        className={`
          relative flex items-center justify-center px-4 py-2
          bg-gradient-to-r from-orange-50/80 via-white/90 to-orange-100/80
          dark:from-[#020102] dark:via-[#1a0f0f] dark:to-[#ff6600]/30
          backdrop-blur-xl border-b border-black/5 dark:border-white/10
          shadow-sm
        `}
      >
        {/* Subtle Background Glows */}
        <div className="absolute top-0 left-1/4 w-32 h-full bg-[#ff6600]/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-0 right-1/4 w-32 h-full bg-orange-400/5 dark:bg-white/5 rounded-full blur-3xl animate-pulse delay-1000"></div>

        {/* Message Slider Container */}
        <div className="relative flex items-center justify-center overflow-hidden w-full max-w-4xl mx-auto px-8 sm:px-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -10, opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="flex items-center justify-center text-center text-[13px] sm:text-sm font-medium tracking-normal text-slate-800 dark:text-neutral-100 py-1"
            >
              {messages[currentIndex]}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Close Button */}
        <button
          onClick={() => setShow(false)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#ff6600] dark:text-neutral-400 dark:hover:text-[#ff6600] transition-colors p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
          aria-label="Close announcement"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
};

export default AnnouncementBar;